/**
 * DecisionLog — ADR list with status pills, sort controls, and Sheet
 * detail panel (RAISE-16886 T5).
 *
 * Uses ?artifact={id} search param for deep-linking. Opens ArtifactViewer
 * inside a Sheet on row click or on mount when param is present.
 * Sheet state is fully controlled by the URL param — no setState-in-effect.
 */

import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGovernanceArtifacts } from "@/hooks/useGovernanceArtifacts";
import { useDecisionLog, type SortKey, type SortDir } from "@/hooks/useDecisionLog";
import { ArtifactViewer } from "@/components/project/ArtifactViewer";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { GovernanceArtifactItem } from "@/hooks/useGovernanceArtifacts";
import type { AdrStatus } from "@/lib/governancePulse";

// ---------------------------------------------------------------------------
// Status pill
// ---------------------------------------------------------------------------

const STATUS_VARIANTS: Record<AdrStatus, string> = {
  accepted: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  draft: "bg-muted text-muted-foreground",
  deprecated: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  superseded: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
};

interface StatusPillProps {
  status: AdrStatus;
}

function StatusPill({ status }: StatusPillProps) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_VARIANTS[status]}`}
    >
      {t(`projectPortal.governance.decisionLog.status.${status}`, {
        defaultValue: status,
      })}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sort header button
// ---------------------------------------------------------------------------

interface SortHeaderProps {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  currentDir: SortDir;
  onSort: (key: SortKey, dir: SortDir) => void;
}

function SortHeader({ label, sortKey, currentKey, currentDir, onSort }: SortHeaderProps) {
  const isActive = sortKey === currentKey;
  const nextDir: SortDir = isActive && currentDir === "asc" ? "desc" : "asc";

  return (
    <button
      type="button"
      onClick={() => onSort(sortKey, nextDir)}
      className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
      aria-label={label}
    >
      {label}
      {isActive && (
        <span className="text-primary">{currentDir === "asc" ? "↑" : "↓"}</span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface DecisionLogProps {
  projectSlug: string;
}

export function DecisionLog({ projectSlug }: DecisionLogProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { artifacts } = useGovernanceArtifacts(projectSlug);
  const { entries, sortKey, sortDir, setSort } = useDecisionLog(artifacts);

  // Derive selected artifact directly from URL param — no setState-in-effect
  const artifactParam = searchParams.get("artifact");
  const selectedArtifact: GovernanceArtifactItem | null = artifactParam
    ? (entries.find((e) => e.artifact.id === artifactParam)?.artifact ?? null)
    : null;
  const sheetOpen = selectedArtifact !== null;

  function openArtifact(artifact: GovernanceArtifactItem) {
    const next = new URLSearchParams(searchParams);
    next.set("artifact", artifact.id);
    setSearchParams(next, { replace: true });
  }

  function closeSheet() {
    const next = new URLSearchParams(searchParams);
    next.delete("artifact");
    setSearchParams(next, { replace: true });
  }

  if (entries.length === 0) {
    return (
      <div className="py-4 text-center">
        <p className="text-sm text-muted-foreground">
          {t("projectPortal.governance.decisionLog.empty", {
            defaultValue: "No ADRs recorded yet.",
          })}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2" data-testid="decision-log">
        {/* Header row with sort buttons */}
        <div className="grid grid-cols-[auto_1fr_auto_auto] gap-3 px-3 items-center">
          <SortHeader
            label={t("projectPortal.governance.decisionLog.colAdr", {
              defaultValue: "ADR#",
            })}
            sortKey="adrNumber"
            currentKey={sortKey}
            currentDir={sortDir}
            onSort={setSort}
          />
          <SortHeader
            label={t("projectPortal.governance.decisionLog.colTitle", {
              defaultValue: "Title",
            })}
            sortKey="title"
            currentKey={sortKey}
            currentDir={sortDir}
            onSort={setSort}
          />
          <SortHeader
            label={t("projectPortal.governance.decisionLog.colStatus", {
              defaultValue: "Status",
            })}
            sortKey="status"
            currentKey={sortKey}
            currentDir={sortDir}
            onSort={setSort}
          />
          <SortHeader
            label={t("projectPortal.governance.decisionLog.colDate", {
              defaultValue: "Date",
            })}
            sortKey="date"
            currentKey={sortKey}
            currentDir={sortDir}
            onSort={setSort}
          />
        </div>

        {/* ADR rows */}
        <ul className="space-y-1">
          {entries.map((entry) => (
            <li key={entry.artifact.id}>
              <button
                type="button"
                onClick={() => openArtifact(entry.artifact)}
                className="w-full grid grid-cols-[auto_1fr_auto_auto] gap-3 px-3 py-2 rounded-md hover:bg-muted/50 text-left items-center transition-colors"
                aria-label={`ADR-${entry.adrNumber} ${entry.title}`}
              >
                <span className="text-xs font-mono text-muted-foreground">
                  {String(entry.adrNumber).padStart(3, "0")}
                </span>
                <span className="text-sm truncate">{entry.title}</span>
                <StatusPill status={entry.status} />
                <span className="text-xs text-muted-foreground">
                  {entry.date.slice(0, 10)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Detail sheet */}
      <Sheet open={sheetOpen} onOpenChange={(open) => { if (!open) closeSheet(); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {selectedArtifact
                ? t("projectPortal.governance.decisionLog.sheetTitle", {
                    defaultValue: "ADR Detail",
                  })
                : ""}
            </SheetTitle>
          </SheetHeader>
          {selectedArtifact && (
            <div className="mt-4">
              <ArtifactViewer artifact={selectedArtifact} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
